import { getAccessToken, login } from "./auth";
import { config } from "./config";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export class ConflictError extends ApiError {
  constructor(message: string) {
    super(412, message);
  }
}

export type StateGetResult<T = unknown> = {
  value: T;
  version: number;
  updatedAt: string;
  updatedBy: string;
} | null;

async function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();
  if (!token) {
    // No valid session; bounce to login. Drops in-flight state, acceptable.
    await login();
    throw new Error("redirecting to login");
  }
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`${config.app.apiBase}${path}`, { ...init, headers });
  if (res.status === 401) {
    await login();
    throw new ApiError(401, "unauthorized");
  }
  return res;
}

export const api = {
  /**
   * Fetch a state key. Returns `null` for 404. On success returns the value
   * plus the current server version, which the caller must echo back as
   * `If-Match` on the next PUT.
   */
  async getState<T = unknown>(key: string): Promise<StateGetResult<T>> {
    const res = await authFetch(`/state/${encodeURIComponent(key)}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new ApiError(res.status, await res.text());
    const body = (await res.json()) as {
      value: T;
      version: number;
      updatedAt: string;
      updatedBy: string;
    };
    return body;
  },

  /**
   * Store a state key. `expectedVersion` is the version the caller last saw
   * for this key (0 for first-time writes). The server uses this as an
   * optimistic-concurrency check and returns 412 if someone else stored in
   * the meantime — surfaced as a `ConflictError`.
   */
  async putState(
    key: string,
    value: unknown,
    expectedVersion: number,
  ): Promise<{ version: number; updatedAt: string }> {
    const res = await authFetch(`/state/${encodeURIComponent(key)}`, {
      method: "PUT",
      headers: { "If-Match": String(expectedVersion) },
      body: JSON.stringify(value),
    });
    if (res.status === 412) {
      throw new ConflictError(await res.text());
    }
    if (!res.ok) throw new ApiError(res.status, await res.text());
    return (await res.json()) as { version: number; updatedAt: string };
  },

  async deleteState(key: string): Promise<void> {
    const res = await authFetch(`/state/${encodeURIComponent(key)}`, {
      method: "DELETE",
    });
    if (!res.ok && res.status !== 404) {
      throw new ApiError(res.status, await res.text());
    }
  },
};
